import { inject } from '@angular/core';
import { AcceptDenyService, CourseService } from '@core/services';
import { map } from 'rxjs/operators';
import { CourseEntity, CoursesCommonEntity, CourseUser, GroupEntity, InstructorEntity, RoomEntity, UserEntity } from '@core/entities';
import { plainToInstance } from 'class-transformer';
import { GenerateImageUrlWithToken, GetTeamId, GetUserId } from '@core/utils';
import { SectionType } from '@core/enums';
import { Observable } from 'rxjs';

export class UseCourse {
  private readonly _courseService = inject(CourseService);
  private readonly _acceptDenyService = inject(AcceptDenyService);

  getAllCourses(): Observable<CourseEntity[]> {
    return this._courseService.find().pipe(
      map((res: any) => {
        return res.map((course: CourseEntity) => {
          const transformedCourse = plainToInstance(CourseEntity, course);
          if (transformedCourse.image) transformedCourse.imageUrl = GenerateImageUrlWithToken(transformedCourse.id, SectionType.COURSE);
          transformedCourse.instructorType = Number(transformedCourse.instructorType);
          transformedCourse.author = Number(transformedCourse.author) || 0;
          transformedCourse.visibility = Number(transformedCourse.visibility);

          if (transformedCourse.room != 'null') {
            transformedCourse.room = Number(transformedCourse.room);
          }

          return transformedCourse as CourseEntity;
        });
      }),
    );
  }

  getCourseById(courseId: number): Observable<CourseEntity> {
    return this._courseService.get(courseId).pipe(
      map((res: any) => {
        const course: CourseEntity = plainToInstance(CourseEntity, res);

        if (course.image) course.imageUrl = GenerateImageUrlWithToken(course.id, SectionType.COURSE);
        course.instructorType = Number(course.instructorType);
        course.author = Number(course.author) || 0;
        course.visibility = Number(course.visibility);
        course.courseUsers = course.courseUsers.filter((user: CourseUser) => user.users);

        if (course.room != 'null') {
          course.room = Number(course.room);
        }

        if (course.tags && typeof course.tags === 'string') {
          course.courseTagsArray = JSON.parse(course.tags);
        }

        return course as CourseEntity;
      }),
    );
  }

  getCoursesCommonInfo(): Observable<CoursesCommonEntity> {
    const teamId = GetTeamId();

    return this._courseService.commonInfo(teamId).pipe(
      map((res: any) => {
        return {
          users: res.users.map((user: UserEntity) => plainToInstance(UserEntity, user)),
          groups: res.groups.map((group: GroupEntity) => plainToInstance(GroupEntity, group)),
          rooms: res.rooms.map((room: RoomEntity) => plainToInstance(RoomEntity, room)),
          instructors: res.instructors.map((instructor: InstructorEntity) => plainToInstance(InstructorEntity, instructor)),
        } as unknown as CoursesCommonEntity;
      }),
    );
  }

  createCourse(formData: FormData) {
    return this._courseService.create(formData);
  }

  updateCourse(courseId: number, formData: FormData) {
    return this._courseService.update(courseId, formData);
  }

  deleteCourse(courseId: number) {
    return this._courseService.delete(courseId);
  }

  deleteCourseFile(courseId: number) {
    return this._courseService.deleteFile(courseId);
  }

  getApprovedParticipants(courseId: number) {
    return this._courseService.getApprovedParticipants(courseId).pipe(
      map((res: any) => {
        let participants = res.map((user: UserEntity) => plainToInstance(CourseUser, user));
        participants = participants.filter((participant: CourseUser) => participant.users);
        return participants;
      }),
    );
  }

  getUnapprovedParticipants(courseId: number) {
    return this._courseService.getUnapprovedParticipants(courseId).pipe(map((res: any) => res.map((user: UserEntity) => plainToInstance(UserEntity, user))));
  }

  joinCourse(courseId: number) {
    return this._courseService.joinCourse(courseId, +GetUserId());
  }

  acceptCourse(courseId: number) {
    return this._acceptDenyService.acceptInvitedCourse(courseId, +GetUserId());
  }

  denyCourse(courseId: number) {
    return this._acceptDenyService.denyInvitedCourse(courseId, +GetUserId());
  }

  downloadDocument(courseId: number, fileName: string, type: 0 | 1 = 0) {
    const data = {
      course_id: courseId,
      name: fileName,
      type,
    };
    return this._courseService.downloadDocument(data);
  }
}
